import { css } from '@emotion/react'
import { Trans } from '@lingui/macro'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { brandColors } from '../../../../../shared/basicPalette'
import { dayjsFromNow } from '../../../../../shared/dayjs'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import {
  Box,
  ButtonOutline,
  Flex,
  Heading,
  Link,
  Text
} from '../../../components'
import { Dropdown } from '../../../components/common/dropdown/Dropdown'
import IconOptionsVertical from '../../../components/common/icon/IconOptionsVertical'
import { IconVersions } from '../../../components/common/icon/IconVersions'
import Spinner from '../../../components/common/loader/Loader'
import { StatusLatestGrey } from '../../../components/common/status/Status'
import {
  PathwayVersionDrawerItemFragment,
  usePathwayVersionsDrawerQuery
} from './PathwayVersionsDrawer.codegen'

const VERSIONS_ITERATION_LIMIT = 10

export const PathwayVersionsDrawerContent = ({
  pathwayId,
  isEditMode = false
}) => {
  const params = useParams<{ pathwayVersion?: string }>()
  const [versions, setVersions] = useState<PathwayVersionDrawerItemFragment[]>(
    []
  )
  const [loadedIterations, setLoadedIterations] = useState(1)
  const { data, loading } = usePathwayVersionsDrawerQuery({
    variables: {
      pathwayId,
      offset: (loadedIterations - 1) * VERSIONS_ITERATION_LIMIT,
      limit: VERSIONS_ITERATION_LIMIT
    },
    onCompleted: (data) => {
      if (data?.admin?.pathway?.__typename === 'PathwayModelAsAdmin') {
        setVersions([...versions, ...data?.admin?.pathway?.pathwayVersions])
      }
    }
  })

  if (data?.admin?.pathway?.__typename !== 'PathwayModelAsAdmin') {
    return null
  }

  if (versions.length === 0 && !loading) {
    return (
      <div className="no-items">
        <IconVersions boxSize="64px" mr="4px" />
        <Heading size="md">
          <Trans>No versions yet</Trans>
        </Heading>
      </div>
    )
  }

  return (
    <>
      {versions.map((pathwayVersion, index) => (
        <PathwayVersionListItem
          key={index}
          pathwayVersion={pathwayVersion}
          pathwayId={pathwayId}
          isLatest={
            data?.admin?.pathway?.production_pathway_version_id ===
            pathwayVersion.id
          }
          isSelected={
            (!params.pathwayVersion &&
              data?.admin?.pathway?.production_pathway_version_id ===
                pathwayVersion.id) ||
            (!!params.pathwayVersion &&
              params.pathwayVersion === pathwayVersion.version)
          }
          isEditMode={isEditMode}
        />
      ))}
      {loading && (
        <Box py="24px">
          <Spinner />
        </Box>
      )}
      {versions.length > 0 &&
        versions.length < (data?.admin?.pathway?.pathwayVersionsCount || 0) && (
          <Box p="12px">
            <ButtonOutline
              size="small"
              onClick={() => setLoadedIterations(loadedIterations + 1)}
            >
              <Trans>Load more</Trans>
            </ButtonOutline>
          </Box>
        )}
    </>
  )
}

const PathwayVersionListItem = ({
  pathwayVersion,
  pathwayId,
  isLatest = false,
  isEditMode = false,
  isSelected = false
}: {
  pathwayVersion: PathwayVersionDrawerItemFragment
  pathwayId: number
  isEditMode: boolean
  isLatest?: boolean
  isSelected?: boolean
}) => {
  return (
    <Link
      as={isEditMode ? 'div' : undefined}
      color={brandColors.grey[900]}
      display="block"
      href={
        isEditMode
          ? undefined
          : isLatest
          ? linkBuilder.pathwayDetail(pathwayId)
          : linkBuilder.pathwayDetailWithVersion(
              pathwayId,
              pathwayVersion?.version,
              true
            )
      }
      cursor={isEditMode ? 'default !important' : 'pointer'}
      _hover={{ bgColor: brandColors.white, textDecoration: 'none' }}
      bgColor={isSelected ? brandColors.white : undefined}
      borderBottom={`1px solid ${brandColors.grey['200']}`}
      p="12px"
      css={css`
        .IconOptionsVertical {
          visibility: hidden;
        }
        &:hover .IconOptionsVertical {
          visibility: ${isEditMode ? 'visible' : 'hidden'};
        }
      `}
    >
      <Flex justify="space-between" align="center">
        <Flex flex={1}>
          <Text fontWeight="700">{pathwayVersion.version}</Text>
          {isLatest && <StatusLatestGrey ml="8px" />}
        </Flex>
        {isEditMode && (
          <Dropdown
            visibility={isEditMode ? 'visible' : 'hidden'}
            tabIndex={undefined}
            positionOfContent="right"
            css={css`
              .Dropdown__content {
                transform: translateY(12px) translateX(10%) !important;
              }
              .Dropdown__content:before {
                left: 91% !important;
              }
            `}
            w="auto"
            content={
              <Link
                whiteSpace="nowrap"
                className="DropdownLink"
                _hover={{ bgColor: brandColors.white, textDecoration: 'none' }}
                isExternal
                color={brandColors.grey[700]}
                href={
                  isLatest
                    ? linkBuilder.pathwayDetail(pathwayId)
                    : linkBuilder.pathwayDetailWithVersion(
                        pathwayId,
                        pathwayVersion?.version,
                        true
                      )
                }
              >
                <IconVersions w="18px" h="16px" />
                <Trans>View pathway version</Trans>
              </Link>
            }
          >
            <IconOptionsVertical color={brandColors.grey.mid} />
          </Dropdown>
        )}
      </Flex>
      <Box pt="8px" color={brandColors.grey.mid} fontSize="sm" as="footer">
        {dayjsFromNow(pathwayVersion?.created_at)}
        {pathwayVersion?.author?.display_name && (
          <Text
            as="span"
            _before={{
              content: '" • "'
            }}
          >
            {pathwayVersion?.author?.display_name}
          </Text>
        )}
      </Box>
    </Link>
  )
}
